import { computed } from 'vue'
import { sdk } from '@/graphql/client'
import { useAuthStore } from '@/stores/auth'
import { useAsyncData } from '@lib/composables/useAsyncData'

export interface LookupOption {
    id: string
    name: string
}

export interface PriceLookupOption extends LookupOption {
    // Bzuco ID ceny — import ho doplní do pole `priceId`, když uživatel zvolí
    // existující cenovou hladinu.
    bzucoId: number | null
}

export interface TicketsetLookupOption extends LookupOption {
    // Parent event id (string) — lets the tickets filter narrow ticketsets to the
    // selected event. Null when the ticketset has no event (shouldn't happen).
    eventId: string | null
    // Bzuco ID sady — doplní se do pole `ticketsetId` při výběru existující sady.
    bzucoId: number | null
    // Cenové hladiny sady — nabídka pro navázaný výběr ceny v importu.
    prices: PriceLookupOption[]
}

/**
 * Loads available events for the active instance (lookup for filter selects + forms).
 *
 * Backend deviations (F5):
 *   - No `instance` argument — multi-tenancy resolved via session.
 *   - `admin.event.list → [EventType]` (plain list, NOT a connection).
 *   - `EventType.id` is `Int` in schema; codegen types it as `number | null`.
 *     We coerce to string to align with `BSelect` option-value (string-based).
 *   - Refetch is triggered when `auth.invalidationKey` changes (after instance switch).
 */
export function useEventOptions() {
    const auth = useAuthStore()
    const { data } = useAsyncData(
        async () => {
            const r = await sdk.AdminLookupEvents()
            const list = r.admin?.event?.list ?? []
            return list
                .filter((e): e is NonNullable<typeof e> => e != null && e.id != null)
                .map(e => ({ id: String(e.id), name: e.name }))
        },
        { watch: [computed(() => auth.invalidationKey)] }
    )
    return computed<LookupOption[]>(() => data.value ?? [])
}

/**
 * Loads available ticketsets for the active instance.
 *
 * Backend deviations: same as `useEventOptions` — plain list, no `instance` arg.
 */
export function useTicketsetOptions() {
    const auth = useAuthStore()
    const { data } = useAsyncData(
        async () => {
            const r = await sdk.AdminLookupTicketsets()
            const list = r.admin?.ticketset?.list ?? []
            return list
                .filter((t): t is NonNullable<typeof t> => t != null && t.id != null)
                .map(t => ({
                    id: String(t.id),
                    name: t.name,
                    eventId: t.event?.id != null ? String(t.event.id) : null,
                    bzucoId: t.bzucoId ?? null,
                    prices: (t.prices ?? [])
                        .filter((p): p is NonNullable<typeof p> => p != null && p.id != null)
                        .map(p => ({ id: String(p.id), name: p.name, bzucoId: p.bzucoId ?? null }))
                }))
        },
        { watch: [computed(() => auth.invalidationKey)] }
    )
    return computed<TicketsetLookupOption[]>(() => data.value ?? [])
}
